import React, { useState, useEffect } from "react";
import shortid from "shortid";
import {
  Autocomplete,
  Box,
  Button,
  CardContent,
  CardHeader,
  CurrencyInput,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  Grid,
  IconButton,
  Loading,
  Paper,
  Stack,
  TextFielDecimalNumbers,
  TextField,
  Tooltip,
  Typography,
} from "../../../../components";
import {
  EquipamentosService,
  InsumosService,
  MaoObraService,
  notification,
  TerceirizadosService,
} from "../../../../services";
import ServicoPrecificacaoService from "../../../../services/api/servico-precificacao.service";
import { formatPrecisaoFloat, formatPrice, responseErros, unformatPrice } from "../../../../utils";
import { value } from "../../../../helpers";

const maoObraService = new MaoObraService();
const insumosService = new InsumosService();
const equipamentosService = new EquipamentosService();
const terceirizadosService = new TerceirizadosService();
const servicoPrecificacaoService = new ServicoPrecificacaoService();

const INITIAL_PARAMS = {
  custoOperacionalIndireto: 0,
  comissao: 0,
  issIbsCbs: 0,
  taxaCartao: 0,
  taxaAdm: 0,
  lucro: 0,
};

function calcularPrecoVenda(custoDireto, params) {
  const {
    custoOperacionalIndireto,
    comissao,
    issIbsCbs,
    taxaCartao,
    taxaAdm,
    lucro,
  } = params;

  const custoIndireto =
    custoDireto * (parseFloat(custoOperacionalIndireto) / 100 || 0);
  const custoTotal = custoDireto + custoIndireto;

  const somaPercentual =
    (parseFloat(comissao) || 0) +
    (parseFloat(issIbsCbs) || 0) +
    (parseFloat(taxaCartao) || 0) +
    (parseFloat(taxaAdm) || 0) +
    (parseFloat(lucro) || 0);

  const divisor = 1 - somaPercentual / 100;
  const precoVenda = divisor > 0 ? custoTotal / divisor : 0;
  const erroCalculo = divisor <= 0;

  return { custoIndireto, custoTotal, precoVenda, erroCalculo };
}

function calcularLucroReverso(precoVendaDesejado, custoTotal, params) {
  const { comissao, issIbsCbs, taxaCartao, taxaAdm } = params;

  const outrosPercentuais =
    (parseFloat(comissao) || 0) +
    (parseFloat(issIbsCbs) || 0) +
    (parseFloat(taxaCartao) || 0) +
    (parseFloat(taxaAdm) || 0);

  const somaPercentual = 100 * (1 - custoTotal / precoVendaDesejado);

  return somaPercentual - outrosPercentuais;
}

function ResumoRow({ label, value, bold, color }) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Typography
        variant="body2"
        sx={{ fontWeight: bold ? 600 : 400, color: color || "text.secondary" }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          borderBottom: "1px dotted grey",
          borderWidth: "0.17rem",
          flexGrow: 2,
          mx: "0.75rem",
        }}
      />
      <Typography
        variant="body2"
        sx={{
          fontWeight: bold ? 700 : 500,
          color: color || "inherit",
          mr: 0.5,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function RowEditable({ item, onEdit, onRemove }) {
  const total =
    (parseFloat(item.vlPeca) || 0) * (parseFloat(item.qtdHoras) || 0);
  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      sx={{ py: 1, borderBottom: "1px solid #f0f0f0" }}
    >
      <Typography variant="body2" sx={{ flex: 2, fontWeight: 500 }}>
        {item.descricao || "—"}
      </Typography>
      <Typography
        variant="body2"
        sx={{ flex: 1, textAlign: "right", color: "#555" }}
      >
        {formatPrice(item.vlPeca || 0, false)}
      </Typography>
      <Typography
        variant="body2"
        sx={{ flex: 1, textAlign: "center", color: "#555" }}
      >
        {item.qtdHoras}
      </Typography>
      <Typography
        variant="body2"
        sx={{ flex: 1, textAlign: "right", fontWeight: 600 }}
      >
        {formatPrice(total, false)}
      </Typography>
      <Tooltip title="Editar">
        <IconButton size="small" onClick={() => onEdit(item)} color="primary">
          <i className="ph ph-pencil" style={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Remover">
        <IconButton
          size="small"
          onClick={() => onRemove(item.id)}
          color="error"
        >
          <i className="ph ph-trash" style={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export function PrecificacaoServicoDialog({
  open,
  setOpen,
  idServico,
  servicoEntity,
  setServico,
}) {
  const [loading, setLoading] = useState(false);
  const [currentItem, setCurrentItem] = useState({});

  const [maoObrasList, setMaoObrasList] = useState([]);
  const [insumosList, setInsumosList] = useState([]);
  const [equipamentosList, setEquipamentosList] = useState([]);
  const [terceirizadosList, setTerceirizadosList] = useState([]);

  const [maoDeObraItens, setMaoDeObraItens] = useState([]);
  const [insumoItens, setInsumoItens] = useState([]);
  const [equipamentoItens, setEquipamentoItens] = useState([]);
  const [maoObraTerceirizadoItens, setMaoObraTerceirizadoItens] = useState([]);

  const [maoObra, setMaoObra] = useState({
    itemId: null,
    vlPeca: "",
    qtdHoras: 1,
  });
  const [insumo, setInsumo] = useState({
    itemId: null,
    vlPeca: "",
    qtdHoras: 1,
  });
  const [equipamento, setEquipamento] = useState({
    itemId: null,
    vlPeca: "",
    qtdHoras: 1,
  });
  const [maoObraTerceirizado, setMaoObraTerceirizado] = useState({
    itemId: null,
    descricao: "",
    vlPeca: "",
    qtdHoras: 1,
  });

  const [editingMaoId, setEditingMaoId] = useState(null);
  const [editingInsumoId, setEditingInsumoId] = useState(null);
  const [editingEquipamentoId, setEditingEquipamentoId] = useState(null);
  const [editingTerceirizadoId, setEditingTerceirizadoId] = useState(null);

  const [params, setParams] = useState({ ...INITIAL_PARAMS });
  const [precoVendaManual, setPrecoVendaManual] = useState(null);

  useEffect(() => {
    if (!open) return;
    setCurrentItem({ ...(servicoEntity || {}) });

    const precificacoes = servicoEntity?.precificacoes || [];
    setMaoDeObraItens(precificacoes.filter((p) => p.tipo === "MAO_DE_OBRA"));
    setInsumoItens(precificacoes.filter((p) => p.tipo === "INSUMO"));
    setEquipamentoItens(
      precificacoes.filter((p) => p.tipo === "EQUIPAMENTO"),
    );
    setMaoObraTerceirizadoItens(
      precificacoes.filter((p) => p.tipo === "TERCEIRIZADO"),
    );

    const paramsIniciais = {
      custoOperacionalIndireto: servicoEntity?.custoOperacionalIndireto || 0,
      comissao: servicoEntity?.comissao || 0,
      issIbsCbs: servicoEntity?.issIbsCbs || 0,
      taxaCartao: servicoEntity?.taxaCartao || 0,
      taxaAdm: servicoEntity?.taxaAdm || 0,
      lucro: servicoEntity?.lucro || 0,
    };
    setParams((prev) => ({ ...prev, ...paramsIniciais }));

    const custoDiretoInicial = precificacoes.reduce(
      (acc, i) =>
        acc + (parseFloat(i.vlPeca) || 0) * (parseFloat(i.qtdHoras) || 0),
      0,
    );
    const { precoVenda: precoCalculadoInicial, erroCalculo: erroInicial } =
      calcularPrecoVenda(custoDiretoInicial, paramsIniciais);
    const precoVigente = parseFloat(servicoEntity?.precoServico) || 0;
    const bateComVigente =
      Math.round(precoCalculadoInicial * 100) === Math.round(precoVigente * 100);

    setPrecoVendaManual(
      precoVigente > 0 && (erroInicial || !bateComVigente)
        ? precoVigente
        : null,
    );

    maoObraService.getAll({ nonPaginated: true }).then((res) => {
      if (!res.isAxiosError) setMaoObrasList(res.data);
    });
    insumosService.getAll({ nonPaginated: true }).then((res) => {
      if (!res.isAxiosError) setInsumosList(res.data);
    });
    equipamentosService.getAll({ nonPaginated: true }).then((res) => {
      if (!res.isAxiosError) setEquipamentosList(res.data);
    });
    terceirizadosService.getAll({ nonPaginated: true }).then((res) => {
      if (!res.isAxiosError) setTerceirizadosList(res.data);
    });
  }, [open, servicoEntity]);

  const totalMaoDeObra = maoDeObraItens.reduce(
    (acc, i) =>
      acc + (parseFloat(i.vlPeca) || 0) * (parseFloat(i.qtdHoras) || 0),
    0,
  );
  const totalInsumos = insumoItens.reduce(
    (acc, i) =>
      acc + (parseFloat(i.vlPeca) || 0) * (parseFloat(i.qtdHoras) || 0),
    0,
  );
  const totalEquipamentos = equipamentoItens.reduce(
    (acc, i) =>
      acc + (parseFloat(i.vlPeca) || 0) * (parseFloat(i.qtdHoras) || 0),
    0,
  );
  const totalTerceirizado = maoObraTerceirizadoItens.reduce(
    (acc, i) =>
      acc + (parseFloat(i.vlPeca) || 0) * (parseFloat(i.qtdHoras) || 0),
    0,
  );
  const custoDireto =
    totalMaoDeObra + totalInsumos + totalEquipamentos + totalTerceirizado;
  const {
    custoIndireto,
    custoTotal,
    precoVenda: precoVendaCalculado,
    erroCalculo,
  } = calcularPrecoVenda(custoDireto, params);
  const precoVenda =
    precoVendaManual !== null ? precoVendaManual : precoVendaCalculado;
  const temErroCalculo = precoVendaManual === null && erroCalculo;

  function onEditarItem({ form, setForm, setEditingId }) {
    setEditingId(form.id);
    setForm({
      itemId: form.itemId || null,
      vlPeca: form.vlPeca,
      qtdHoras: form.qtdHoras,
    });
  }

  function sairModoManual() {
    if (precoVendaManual === null) return;
    setPrecoVendaManual(null);
    setParams((prev) => ({ ...prev, lucro: INITIAL_PARAMS.lucro }));
  }

  function adicionarItem({
    form,
    editingId,
    setItems,
    setEditingId,
    setForm,
    tipo,
    descricaoPadrao,
  }) {
    const vlPeca = parseFloat(form.vlPeca) || 0;
    const qtdHoras = parseFloat(form.qtdHoras) || 1;
    if (!vlPeca) return;
    sairModoManual();
    const item = {
      descricao: form?.descricao || descricaoPadrao,
      vlPeca,
      qtdHoras,
      itemId: form.itemId || null,
    };

    if (editingId) {
      setItems((prev) =>
        prev.map((i) =>
          i.id === editingId
            ? {
                ...i,
                ...item,
              }
            : i,
        ),
      );

      setEditingId(null);
    } else {
      setItems((prev) => [
        ...prev,
        {
          id: shortid.generate(),
          tipo,
          ...item,
        },
      ]);
    }

    setForm({
      itemId: null,
      vlPeca: "",
      qtdHoras: 1,
    });
  }

  const handleParam = (name, value) => {
    setPrecoVendaManual(null);
    setParams((prev) => ({ ...prev, [name]: unformatPrice(value) }));
  };

  const changeUnformat = (e, setter) => {
    setter(unformatPrice(e?.target?.value));
  };

  function removerItem(setItems, id) {
    sairModoManual();
    setItems((prev) => prev.filter((i) => i.id !== id));
  }

  function handlePrecoVendaBlur(e) {
    const novoPreco = parseFloat(unformatPrice(e?.target?.value)) || 0;
    if (novoPreco <= 0) {
      setPrecoVendaManual(null);
      return;
    }
    setPrecoVendaManual(novoPreco);
    const novoLucro = calcularLucroReverso(novoPreco, custoTotal, params);
    setParams((prev) => ({ ...prev, lucro: novoLucro }));
  }

  async function handleSalvar() {
    setLoading(true);
    try {
      const itens = [
        ...maoDeObraItens,
        ...insumoItens,
        ...equipamentoItens,
        ...maoObraTerceirizadoItens,
      ].map((i) => ({
        id: i.id,
        estServicoId: servicoEntity?.id ?? null,
        itemId: i.itemId || null,
        tipo: i.tipo,
        qtdHoras: parseFloat(i.qtdHoras) || 0,
        vlPeca: parseFloat(i.vlPeca) || 0,
        descricao: i.descricao || "",
      }));

      const servicoUpdate = {
        id: servicoEntity?.id,
        custoOperacionalIndireto:
          parseFloat(params.custoOperacionalIndireto) || 0,
        comissao: parseFloat(params.comissao) || 0,
        issIbsCbs: parseFloat(params.issIbsCbs) || 0,
        taxaCartao: parseFloat(params.taxaCartao) || 0,
        taxaAdm: parseFloat(params.taxaAdm) || 0,
        lucro: parseFloat(params.lucro) || 0,
        precoServico: formatPrecisaoFloat(precoVenda),
      };

      if (!idServico) {
        servicoEntity.setPrecificacao(itens, servicoUpdate);
        setServico({ ...servicoEntity });
        notification.sucessoGenericos(
          "Precificação aplicada. Salve o serviço para concluir.",
        );
        setOpen(false);
        return;
      }

      const body = {
        idServico,
        precificacoes: itens.map(({ id, ...item }) =>
          typeof id === "number" ? { ...item, id } : item,
        ),
        servico: servicoUpdate,
      };

      const res = await servicoPrecificacaoService.upsert(body);
      if (!res.isAxiosError) {
        servicoEntity.setPrecificacao(itens, servicoUpdate);
        setServico({ ...servicoEntity });
        notification.alteracaoSucesso();
        setOpen(false);
      } else {
        responseErros(res);
      }
    } catch (err) {
      responseErros(err);
    } finally {
      setLoading(false);
    }
  }

  function handleClose() {
    setOpen(false);
  }

  const paperSx = {
    p: 3,
    border: "0.5px solid #e0e0e0",
    borderRadius: 4,
    backgroundColor: "#ffffff",
    boxShadow: "none !important",
  };

  const SectionTitle = ({ icon, title }) => (
    <Box display="flex" alignItems="center" gap={1} mb={2}>
      <i className={icon} style={{ fontSize: 20, color: "#1976d2" }} />
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
    </Box>
  );

  return (
    <React.Fragment>
      <Dialog
        fullWidth
        maxWidth="xl"
        PaperComponent="div"
        PaperProps={{
          style: { borderRadius: "0.7rem", background: "#ffffff" },
        }}
        open={open}
        onClose={handleClose}
      >
        <DialogContent>
          <CardHeader
            title="Precificação de Serviços"
            subheader={
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {currentItem?.descricao}
              </Typography>
            }
            sx={{ padding: "1rem 1.6rem" }}
          />

          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={7}>
                <Stack spacing={3}>
                  <Paper sx={paperSx}>
                    <SectionTitle
                      icon="ph ph-user-gear"
                      title="Grupo de Atividade (Mão de Obra)"
                    />
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined" fullWidth>
                          <Autocomplete
                            options={maoObrasList}
                            getOptionLabel={(o) => o.descricao || ""}
                            noOptionsText="Sem opções"
                            value={value.autoComplete(
                              maoObrasList,
                              maoObra.itemId,
                            )}
                            onChange={(_, v) =>
                              setMaoObra((prev) => ({
                                ...prev,
                                descricao: v?.descricao || "",
                                itemId: v?.id,
                                vlPeca: v?.custoHora || 0,
                              }))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Selecionar Mão de Obra"
                                variant="outlined"
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <CurrencyInput
                          label="Vl. Mão de Obra"
                          value={maoObra.vlPeca}
                          onBlur={(e) =>
                            changeUnformat(e, (v) =>
                              setMaoObra((prev) => ({ ...prev, vlPeca: v })),
                            )
                          }
                          onChange={(e) =>
                            changeUnformat(e, (v) =>
                              setMaoObra((prev) => ({ ...prev, vlPeca: v })),
                            )
                          }
                          margin="none"
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <TextField
                          label="Qtd. Horas"
                          type="number"
                          value={maoObra.qtdHoras}
                          onChange={(e) =>
                            setMaoObra((prev) => ({
                              ...prev,
                              qtdHoras: e.target.value,
                            }))
                          }
                          fullWidth
                          variant="outlined"
                          inputProps={{ min: 0, step: 0.5 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={1}>
                        <Button
                          variant="contained"
                          disableElevation
                          color={editingMaoId ? "success" : "primary"}
                          onClick={() =>
                            adicionarItem({
                              form: maoObra,
                              editingId: editingMaoId,
                              setItems: setMaoDeObraItens,
                              setEditingId: setEditingMaoId,
                              setForm: setMaoObra,
                              tipo: "MAO_DE_OBRA",
                              descricaoPadrao: "MÃO DE OBRA",
                            })
                          }
                          disabled={!parseFloat(maoObra.vlPeca)}
                          sx={{
                            height: "53px",
                            width: "53px",
                            minWidth: "53px",
                            px: 0,
                          }}
                        >
                          {editingMaoId ? (
                            <i
                              className="ph ph-check"
                              style={{ fontSize: 16, color: "#fff" }}
                            />
                          ) : (
                            <i
                              className="ph ph-plus"
                              style={{ fontSize: 16, color: "#fff" }}
                            />
                          )}
                        </Button>
                      </Grid>
                    </Grid>

                    {maoDeObraItens.length > 0 && (
                      <Box mt={2}>
                        <Box
                          display="flex"
                          gap={1}
                          sx={{
                            pb: 1,
                            borderBottom: "2px solid #e0e0e0",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ flex: 2, fontWeight: 700, color: "#555" }}
                          >
                            Métrica
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              flex: 1,
                              fontWeight: 700,
                              color: "#555",
                              textAlign: "right",
                            }}
                          >
                            Vl. Mão de Obra
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              flex: 1,
                              fontWeight: 700,
                              color: "#555",
                              textAlign: "center",
                            }}
                          >
                            Qtd. Horas
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              flex: 1,
                              fontWeight: 700,
                              color: "#555",
                              textAlign: "right",
                            }}
                          ></Typography>
                          <Box sx={{ width: 32 }} />
                        </Box>
                        {maoDeObraItens.map((item) => (
                          <RowEditable
                            key={item.id}
                            item={item}
                            onEdit={(it) =>
                              onEditarItem({
                                form: it,
                                setForm: setMaoObra,
                                setEditingId: setEditingMaoId,
                              })
                            }
                            onRemove={(id) =>
                              removerItem(setMaoDeObraItens, id)
                            }
                          />
                        ))}
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1,
                            fontWeight: 700,
                            color: "#1976d2",
                            textAlign: "right",
                          }}
                        >
                          Total Mão de Obra:{" "}
                          {formatPrice(totalMaoDeObra, false)}
                        </Typography>
                      </Box>
                    )}
                  </Paper>

                  <Paper sx={paperSx}>
                    <SectionTitle icon="ph ph-package" title="Insumos" />
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined" fullWidth>
                          <Autocomplete
                            options={insumosList}
                            getOptionLabel={(o) => o.descricao || ""}
                            noOptionsText="Sem opções"
                            value={value.autoComplete(
                              insumosList,
                              insumo.itemId,
                            )}
                            onChange={(_, v) =>
                              setInsumo((prev) => ({
                                ...prev,
                                itemId: v?.id || null,
                                descricao: v?.descricao || "",
                                vlPeca: v?.valorTotal || 0,
                              }))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Selecionar Insumo"
                                variant="outlined"
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <CurrencyInput
                          label="Vl. Insumo"
                          value={insumo.vlPeca}
                          onBlur={(e) =>
                            changeUnformat(e, (v) =>
                              setInsumo((prev) => ({ ...prev, vlPeca: v })),
                            )
                          }
                          onChange={(e) =>
                            changeUnformat(e, (v) =>
                              setInsumo((prev) => ({ ...prev, vlPeca: v })),
                            )
                          }
                          fullWidth
                          margin="none"
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <TextField
                          label="Quantidade"
                          type="number"
                          value={insumo.qtdHoras}
                          onChange={(e) =>
                            setInsumo((prev) => ({
                              ...prev,
                              qtdHoras: e.target.value,
                            }))
                          }
                          fullWidth
                          variant="outlined"
                          inputProps={{ min: 0, step: 0.5 }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={1}>
                        <Button
                          variant="contained"
                          disableElevation
                          color={editingInsumoId ? "success" : "primary"}
                          onClick={() =>
                            adicionarItem({
                              form: insumo,
                              editingId: editingInsumoId,
                              setItems: setInsumoItens,
                              setEditingId: setEditingInsumoId,
                              setForm: setInsumo,
                              tipo: "INSUMO",
                              descricaoPadrao: "INSUMO",
                            })
                          }
                          disabled={!parseFloat(insumo.vlPeca)}
                          sx={{
                            height: "53px",
                            width: "53px",
                            minWidth: "53px",
                            px: 0,
                          }}
                        >
                          {editingInsumoId ? (
                            <i
                              className="ph ph-check"
                              style={{ fontSize: 16, color: "#fff" }}
                            />
                          ) : (
                            <i
                              className="ph ph-plus"
                              style={{ fontSize: 16, color: "#fff" }}
                            />
                          )}
                        </Button>
                      </Grid>
                    </Grid>
                    {insumoItens.length > 0 && (
                      <Box mt={2}>
                        {insumoItens.map((item) => (
                          <RowEditable
                            key={item.id}
                            item={item}
                            onEdit={(it) =>
                              onEditarItem({
                                form: it,
                                setForm: setInsumo,
                                setEditingId: setEditingInsumoId,
                              })
                            }
                            onRemove={(id) =>
                              removerItem(setInsumoItens, id)
                            }
                          />
                        ))}
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1,
                            fontWeight: 700,
                            color: "#1976d2",
                            textAlign: "right",
                          }}
                        >
                          Total Insumos: {formatPrice(totalInsumos, false)}
                        </Typography>
                      </Box>
                    )}
                  </Paper>

                  <Paper sx={paperSx}>
                    <SectionTitle icon="ph ph-wrench" title="Equipamentos" />
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined" fullWidth>
                          <Autocomplete
                            options={equipamentosList}
                            getOptionLabel={(o) => o.descricao || ""}
                            noOptionsText="Sem opções"
                            value={value.autoComplete(
                              equipamentosList,
                              equipamento.itemId,
                            )}
                            onChange={(_, v) =>
                              setEquipamento((prev) => ({
                                ...prev,
                                itemId: v?.id || null,
                                descricao: v?.descricao || "",
                                vlPeca: v?.custoHora || 0,
                              }))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Selecionar Equipamento"
                                variant="outlined"
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <CurrencyInput
                          label="Vl. Equipamento"
                          value={equipamento.vlPeca}
                          onBlur={(e) =>
                            changeUnformat(e, (v) =>
                              setEquipamento((prev) => ({
                                ...prev,
                                vlPeca: v,
                              })),
                            )
                          }
                          onChange={(e) =>
                            changeUnformat(e, (v) =>
                              setEquipamento((prev) => ({
                                ...prev,
                                vlPeca: v,
                              })),
                            )
                          }
                          fullWidth
                          margin="none"
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <TextField
                          label="Quantidade"
                          type="number"
                          value={equipamento.qtdHoras}
                          onChange={(e) =>
                            setEquipamento((prev) => ({
                              ...prev,
                              qtdHoras: e.target.value,
                            }))
                          }
                          fullWidth
                          variant="outlined"
                          inputProps={{ min: 0, step: 0.5 }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={1}>
                        <Button
                          variant="contained"
                          disableElevation
                          color={editingEquipamentoId ? "success" : "primary"}
                          onClick={() =>
                            adicionarItem({
                              form: equipamento,
                              editingId: editingEquipamentoId,
                              setItems: setEquipamentoItens,
                              setEditingId: setEditingEquipamentoId,
                              setForm: setEquipamento,
                              tipo: "EQUIPAMENTO",
                              descricaoPadrao: "EQUIPAMENTO",
                            })
                          }
                          disabled={!parseFloat(equipamento.vlPeca)}
                          sx={{
                            height: "53px",
                            width: "53px",
                            minWidth: "53px",
                            px: 0,
                          }}
                        >
                          {editingEquipamentoId ? (
                            <i
                              className="ph ph-check"
                              style={{ fontSize: 16, color: "#fff" }}
                            />
                          ) : (
                            <i
                              className="ph ph-plus"
                              style={{ fontSize: 16, color: "#fff" }}
                            />
                          )}
                        </Button>
                      </Grid>
                    </Grid>
                    {equipamentoItens.length > 0 && (
                      <Box mt={2}>
                        {equipamentoItens.map((item) => (
                          <RowEditable
                            key={item.id}
                            item={item}
                            onEdit={(it) =>
                              onEditarItem({
                                form: it,
                                setForm: setEquipamento,
                                setEditingId: setEditingEquipamentoId,
                              })
                            }
                            onRemove={(id) =>
                              removerItem(setEquipamentoItens, id)
                            }
                          />
                        ))}
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1,
                            fontWeight: 700,
                            color: "#1976d2",
                            textAlign: "right",
                          }}
                        >
                          Total Equipamentos:{" "}
                          {formatPrice(totalEquipamentos, false)}
                        </Typography>
                      </Box>
                    )}
                  </Paper>

                  <Paper sx={paperSx}>
                    <SectionTitle
                      icon="ph ph-users-three"
                      title="Mão de Obra Terceirizada"
                    />
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          options={terceirizadosList}
                          getOptionLabel={(o) => o.descricao || ""}
                          noOptionsText="Sem opções"
                          value={value.autoComplete(
                            terceirizadosList,
                            maoObraTerceirizado.itemId,
                          )}
                          onChange={(_, v) =>
                            setMaoObraTerceirizado((prev) => ({
                              ...prev,
                              itemId: v?.id || null,
                              descricao: v?.descricao || "",
                              vlPeca: v?.valorServico || 0,
                            }))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Selecionar Terceirizado"
                              variant="outlined"
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <CurrencyInput
                          label="Vl. Mão de Obra Terceirizada"
                          value={maoObraTerceirizado.vlPeca}
                          onBlur={(e) =>
                            changeUnformat(e, (v) =>
                              setMaoObraTerceirizado((prev) => ({
                                ...prev,
                                vlPeca: v,
                              })),
                            )
                          }
                          onChange={(e) =>
                            changeUnformat(e, (v) =>
                              setMaoObraTerceirizado((prev) => ({
                                ...prev,
                                vlPeca: v,
                              })),
                            )
                          }
                          margin="none"
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <TextField
                          label="Quantidade"
                          type="number"
                          value={maoObraTerceirizado.qtdHoras}
                          onChange={(e) =>
                            setMaoObraTerceirizado((prev) => ({
                              ...prev,
                              qtdHoras: e.target.value,
                            }))
                          }
                          fullWidth
                          variant="outlined"
                          inputProps={{ min: 0, step: 0.5 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={1}>
                        <Button
                          variant="contained"
                          disableElevation
                          color={editingTerceirizadoId ? "success" : "primary"}
                          onClick={() =>
                            adicionarItem({
                              form: maoObraTerceirizado,
                              editingId: editingTerceirizadoId,
                              setItems: setMaoObraTerceirizadoItens,
                              setEditingId: setEditingTerceirizadoId,
                              setForm: setMaoObraTerceirizado,
                              tipo: "TERCEIRIZADO",
                              descricaoPadrao: "TERCEIRIZADO",
                            })
                          }
                          disabled={!parseFloat(maoObraTerceirizado.vlPeca)}
                          sx={{
                            height: "53px",
                            width: "53px",
                            minWidth: "53px",
                            px: 0,
                          }}
                        >
                          {editingTerceirizadoId ? (
                            <i
                              className="ph ph-check"
                              style={{ fontSize: 16, color: "#fff" }}
                            />
                          ) : (
                            <i
                              className="ph ph-plus"
                              style={{ fontSize: 16, color: "#fff" }}
                            />
                          )}
                        </Button>
                      </Grid>
                    </Grid>
                    {maoObraTerceirizadoItens.length > 0 && (
                      <Box mt={2}>
                        {maoObraTerceirizadoItens.map((item) => (
                          <RowEditable
                            key={item.id}
                            item={item}
                            onEdit={(it) =>
                              onEditarItem({
                                form: it,
                                setForm: setMaoObraTerceirizado,
                                setEditingId: setEditingTerceirizadoId,
                              })
                            }
                            onRemove={(id) =>
                              removerItem(setMaoObraTerceirizadoItens, id)
                            }
                          />
                        ))}
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1,
                            fontWeight: 700,
                            color: "#1976d2",
                            textAlign: "right",
                          }}
                        >
                          Total Terceirizado:{" "}
                          {formatPrice(totalTerceirizado, false)}
                        </Typography>
                      </Box>
                    )}
                  </Paper>

                  <Paper sx={paperSx}>
                    <SectionTitle
                      icon="ph ph-gear"
                      title="Custo Operacional Indireto"
                    />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextFielDecimalNumbers
                          label="Custo Administrativo (%)"
                          name="custoOperacionalIndireto"
                          value={params.custoOperacionalIndireto || ""}
                          onBlur={(e) =>
                            handleParam(
                              "custoOperacionalIndireto",
                              e.target.value,
                            )
                          }
                          onChange={(e) =>
                            handleParam(
                              "custoOperacionalIndireto",
                              e.target.value,
                            )
                          }
                          fullWidth
                          margin="none"
                          size="normal"
                          variant="outlined"
                          inputProps={{ min: 0, max: 100 }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>

                  <Paper sx={paperSx}>
                    <SectionTitle
                      icon="ph ph-percent"
                      title="Despesas Variáveis sobre a Venda"
                    />
                    <Grid container spacing={2}>
                      {[
                        { key: "comissao", label: "Comissão (%)" },
                        { key: "issIbsCbs", label: "ISS/IBS/CBS (%)" },
                        { key: "taxaCartao", label: "Taxa Cartão (%)" },
                        { key: "taxaAdm", label: "Taxa Administrativa (%)" },
                      ].map(({ key, label }) => (
                        <Grid item xs={6} sm={3} key={key}>
                          <TextFielDecimalNumbers
                            label={label}
                            name={key}
                            value={params[key] || ""}
                            onBlur={(e) => handleParam(key, e.target.value)}
                            onChange={(e) => handleParam(key, e.target.value)}
                            fullWidth
                            size="normal"
                            margin="none"
                            variant="outlined"
                            inputProps={{ min: 0, max: 100 }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>

                  <Paper sx={paperSx}>
                    <SectionTitle
                      icon="ph ph-trend-up"
                      title="Margem de Contribuição"
                    />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextFielDecimalNumbers
                          label="Lucro (%)"
                          name="lucro"
                          value={params.lucro || ""}
                          onBlur={(e) => handleParam("lucro", e.target.value)}
                          onChange={(e) => handleParam("lucro", e.target.value)}
                          fullWidth
                          size="normal"
                          margin="none"
                          variant="outlined"
                          inputProps={{ min: 0, max: 99.99 }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Stack>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Paper
                  sx={{
                    p: 3,
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #e0e0e0",
                    borderRadius: 4,
                    position: "sticky",
                    top: 20,
                    boxShadow: "none !important",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <i
                      className="ph ph-calculator"
                      style={{ fontSize: 20, color: "#1976d2" }}
                    />
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "#1a1a1a" }}
                    >
                      Resumo da Precificação
                    </Typography>
                  </Box>

                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: "#555", mb: 1 }}
                  >
                    Custos Diretos
                  </Typography>
                  <Stack spacing={0.75} mb={1.5}>
                    <ResumoRow
                      label="Mão de Obra:"
                      value={`R$ ${formatPrice(totalMaoDeObra, false)}`}
                    />
                    <ResumoRow
                      label="Terceirizado:"
                      value={`R$ ${formatPrice(totalTerceirizado, false)}`}
                    />
                    {totalInsumos > 0 && (
                      <ResumoRow
                        label="Insumos:"
                        value={`R$ ${formatPrice(totalInsumos, false)}`}
                      />
                    )}
                    {totalEquipamentos > 0 && (
                      <ResumoRow
                        label="Equipamentos:"
                        value={`R$ ${formatPrice(totalEquipamentos, false)}`}
                      />
                    )}
                  </Stack>

                  <Divider sx={{ my: 1 }} />

                  <Stack spacing={0.75} mb={1.5}>
                    <ResumoRow
                      label="Custo Direto:"
                      value={`R$ ${formatPrice(custoDireto, false)}`}
                      bold
                    />
                    <ResumoRow
                      label={`Custo Indireto (${
                        params.custoOperacionalIndireto || 0
                      }%):`}
                      value={`R$ ${formatPrice(custoIndireto, false)}`}
                    />
                  </Stack>

                  <Box
                    sx={{
                      backgroundColor: "#e3f2fd",
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      mb: 2,
                    }}
                  >
                    <ResumoRow
                      label="Custo Total:"
                      value={`R$ ${formatPrice(custoTotal, false)}`}
                      bold
                      color="#1565c0"
                    />
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: "#555", mb: 1 }}
                  >
                    Despesas sobre Venda
                  </Typography>
                  <Stack spacing={0.75} mb={1.5}>
                    {[
                      { label: "Comissão", key: "comissao" },
                      { label: "ISS/IBS/CBS", key: "issIbsCbs" },
                      { label: "Taxa Cartão", key: "taxaCartao" },
                      { label: "Taxa Adm.", key: "taxaAdm" },
                    ].map(({ label, key }) => {
                      const percent = parseFloat(params[key]) || 0;
                      const valor = precoVenda
                        ? precoVenda * (percent / 100)
                        : 0;
                      return (
                        <ResumoRow
                          key={key}
                          label={`${label} (${percent}%)`}
                          value={`R$ ${formatPrice(valor, false)}`}
                        />
                      );
                    })}
                  </Stack>

                  {(() => {
                    const percentLucro = parseFloat(params.lucro) || 0;
                    const valorLucro = precoVenda
                      ? precoVenda * (percentLucro / 100)
                      : 0;
                    return (
                      <ResumoRow
                        label={`Lucro (${percentLucro.toFixed(2)}%)`}
                        value={`R$ ${formatPrice(valorLucro, false)}`}
                        bold
                      />
                    );
                  })()}

                  <Divider sx={{ my: 2 }} />

                  <Box
                    sx={{
                      backgroundColor: temErroCalculo ? "#ffebee" : "#e8f5e9",
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: temErroCalculo ? "#c62828" : "#2e7d32",
                        mb: 0.5,
                      }}
                    >
                      Preço de Venda:
                    </Typography>
                    <CurrencyInput
                      value={precoVenda}
                      onFocus={() => {
                        if (precoVendaManual === null) {
                          setPrecoVendaManual(precoVenda);
                        }
                      }}
                      onChange={(e) =>
                        changeUnformat(e, setPrecoVendaManual)
                      }
                      onBlur={handlePrecoVendaBlur}
                      margin="none"
                      size="small"
                      variant="outlined"
                    />
                  </Box>

                  {temErroCalculo && (
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, color: "error.main", display: "block" }}
                    >
                      Verifique os percentuais — a soma pode ser igual ou
                      superior a 100%.
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </CardContent>

          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={handleClose} variant="contained" color="padrao">
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSalvar}
              disabled={loading || temErroCalculo}
            >
              {loading ? "Salvando..." : "Salvar Precificação"}
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
      <Loading loading={loading} />
    </React.Fragment>
  );
}
